import { useState, useRef, useEffect } from "react";
import { useUser } from "@hooks/useUser";
import { usePage } from "@hooks/usePage";
import { Avatar, CircularProgress, IconButton } from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import Button from "@commons/Button";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { uploadProfilePicture } from "@services/users/updateProfile";
import { compressImage, isImageFile } from "@utils/compressor";
import { useGetUserById } from "@services/users/getById";
import { useNavigate } from "react-router-dom";
import Heading from "@commons/Typography/Heading";
import Text from "@commons/Typography/Text";
import EmailIcon from "@mui/icons-material/Email";
import LogoutIcon from "@mui/icons-material/Logout";

export default function ProfileScreen() {
  const { signOut, user, saveUser } = useUser();
  const { setIsLoading } = usePage();
  const navigate = useNavigate();

  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    data,
    isLoading: loadingData,
    error,
    mutate,
  } = useGetUserById(user?.id ?? null);

  const handleImageUpload = async (file: File) => {
    if (!data) return;

    if (!isImageFile(file)) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    try {
      setUploading(true);

      const compressedFile = await compressImage(file);

      const response = await uploadProfilePicture({
        userId: data.id,
        file: compressedFile,
      });

      mutate();

      saveUser({ ...data, profilePictureUrl: response.profilePictureUrl });
      toast.success("Foto de perfil atualizada!");
    } catch (error) {
      const isAppError = error instanceof AppError;
      const message = isAppError
        ? error.message
        : "Erro ao atualizar foto de perfil";
      toast.error(message);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Por favor, selecione uma imagem válida");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("A imagem deve ter no máximo 5MB");
      return;
    }

    handleImageUpload(file);
  };

  const handleImagePick = () => {
    fileInputRef.current?.click();
  };

  const handleLogoff = async () => {
    try {
      setIsLoading(true);
      await signOut();
      toast.success("Sessão encerrada com sucesso!");
    } catch {
      toast.error("Falha ao encerrar sessão");
    } finally {
      setIsLoading(false);
    }
  };

  const getUserInitials = (username?: string): string => {
    if (!username) return "??";

    const words = username.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[words.length - 1][0]).toUpperCase();
    }
    return username.substring(0, 2).toUpperCase();
  };

  useEffect(() => {
    if (error && !loadingData) {
      toast.error("Erro ao carregar dados do perfil");
    }

    if (!data && !loadingData) {
      toast.error("Dados do usuário não encontrados");
      navigate("/");
    }
  }, [error, loadingData, navigate, data]);

  if (!data) {
    return null;
  }
  const userInitials = getUserInitials(data.username);

  if (loadingData) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <p>Carregando...</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow-md rounded-2xl p-6 sm:p-12">
      <div className="max-w-lg mx-auto text-center flex flex-col gap-4">
        <div className="flex items-center justify-center gap-4">
          <div className="relative">
            <Avatar
              src={data.profilePictureUrl || undefined}
              alt={data.username}
              className="w-36 h-36 bg-gray-300"
            >
              {uploading ? (
                <CircularProgress size={24} className="text-primary" />
              ) : (
                <span className="text-xl font-semibold text-gray-600">
                  {userInitials}
                </span>
              )}
            </Avatar>

            {!uploading && (
              <IconButton
                onClick={handleImagePick}
                className="absolute bottom-0 right-0 bg-white border border-gray-200 w-10 h-10 hover:bg-gray-50"
              >
                <CameraAltIcon
                  className="text-primary"
                  style={{ fontSize: 22 }}
                />
              </IconButton>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
          </div>
        </div>
        <div className="flex flex-col">
          <Heading>{data.username}</Heading>
          <Text customVariant="gray">{data.role}</Text>
        </div>

        <div className="flex justify-center items-center gap-2">
          <EmailIcon />
          <Text className="underline">{data.email}</Text>
        </div>

        <Button
          customVariant="danger"
          size="auto"
          onClick={handleLogoff}
          startIcon={<LogoutIcon />}
          className="mt-6"
        >
          Sair
        </Button>
      </div>
    </div>
  );
}
